import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { fidoV2 as fido, browser, unauthenticatedLogger } from '@bsd/ui-utils';
import { errorTracking } from '@bsd/ui-utils/tracking';

// actions
import { actions as appActions, types } from 'modules/application';

// constants
import { API_ERROR_CODES } from 'modules/application/constants';

// selectors
import {
  getApplication,
  getApplicationLoaded,
  getFidoConfig,
  getLoggerConfig,
} from 'modules/application/selectors';

// utils
import {
  getErrorTrackingMessage,
  getRegistrationErrorTrackingMessage,
} from 'utils/util';

import { API_ROUTES, API_KEYS } from 'endpoints';

function* updateSessionIdWorker() {
  const { sessionDurationInMinutes } = yield select(getApplication);
  const cookieName = 'cb_ucid';
  const sessionIdCookie = browser.getCookie(cookieName);
  const sessionId = sessionIdCookie || uuid();
  const date = new Date(Date.now() + sessionDurationInMinutes * 60 * 1000);

  document.cookie = `${cookieName}=${sessionId}; expires=${date.toGMTString()}; domain=comcast.com`;

  yield put(appActions.setSessionId({ sessionId }));
}

// Workers
// This Worker is intended for running absolutely mission-critical operations
// for rest of application to work correctly.
function* appInitWorker() {
  /* ensure session is set before getting fido config
   * and initializing fido in order to get tracking id */
  const { session: isSessionLoaded } = yield select(getApplicationLoaded);
  if (!isSessionLoaded) {
    yield take(types.APP_SESSION_ID_SET);
  }
  const fidoConfig = yield select(getFidoConfig);
  const loggerConfig = yield select(getLoggerConfig);
  loggerConfig.appSettings.loggingEndpoint.url =
    API_ROUTES[API_KEYS.logging].endpoints.log.url;
  yield call(unauthenticatedLogger.config, loggerConfig);
  yield call(fido.init, {
    ...fidoConfig,
    loggingProvider: unauthenticatedLogger,
  });
}

function* handleErrorTracking({ errorCodes = [], pageError = {} }) {
  switch (errorCodes[0]) {
    case API_ERROR_CODES.accountAlreadyRegistered:
    case API_ERROR_CODES.invalidCaptcha:
    case API_ERROR_CODES.invalidJWT:
    case API_ERROR_CODES.inviteExpirationNotSet:
    case API_ERROR_CODES.inviteExpiredOrNotFound:
    case API_ERROR_CODES.passwordBanned:
    case API_ERROR_CODES.passwordInvalid:
    case API_ERROR_CODES.securityQuestionInvalid:
    case API_ERROR_CODES.userPreviouslyRegistered:
    case API_ERROR_CODES.zipCodeMismatch:
      yield call(
        errorTracking,
        '',
        getRegistrationErrorTrackingMessage(errorCodes)
      );
      break;
    default:
      yield call(
        errorTracking,
        '',
        getErrorTrackingMessage(errorCodes, pageError)
      );
  }
}

export const workers = {
  appInitWorker,
  handleErrorTracking,
  updateSessionIdWorker,
};

// Watchers
function* appInitWatch() {
  yield all([
    takeLatest(types.APP_INIT, appInitWorker),
    takeLatest(types.APP_SESSION_ID_UPDATE, updateSessionIdWorker),
  ]);
}

export const watchers = {
  appInitWatch,
};

// Sagas
export default function* rootSaga() {
  yield appInitWatch();
}
